import React, { useEffect, useMemo, useState } from "react";
import { api, ROLE_LABELS, CATEGORY_LABELS, STATUS_LABELS, PRIORITY_LABELS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Users, MessageSquare, Activity, ShieldCheck, PlaneTakeoff, Clock, TrendingUp, MapPin, Stethoscope, ChevronRight } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";
import { Link } from "react-router-dom";

/** Colori coerenti con il brand Synappsi */
const COLORS = {
  primary: "#1E3A8A",
  emerald: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
  sky: "#0EA5E9",
  purple: "#8B5CF6",
  slate: "#64748B",
};
const PALETTE = ["#1E3A8A", "#10B981", "#F59E0B", "#EF4444", "#0EA5E9", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#6366F1"];

function KpiCard({ icon: Icon, label, value, sub, color = "text-[#1E3A8A]", testid, to, cta }) {
  const inner = (
    <>
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center bg-slate-50 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
          <div className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-0.5 tabular-nums" data-testid={`${testid}-value`}>
            {value}
          </div>
          {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
        {to && (
          <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-[#1E3A8A] group-hover:translate-x-0.5 transition-all mt-2 flex-shrink-0" />
        )}
      </div>
      {to && cta && (
        <div className="mt-2 pt-2 border-t border-slate-100 text-[11px] font-semibold text-[#1E3A8A] group-hover:text-[#10B981] transition-colors flex items-center gap-1">
          {cta}
        </div>
      )}
    </>
  );
  const base = "block p-4 md:p-5 bg-white border rounded-2xl transition-all";
  const cls = to
    ? `${base} border-slate-200 hover:border-[#10B981]/40 hover:shadow-md hover:shadow-emerald-500/10 cursor-pointer group`
    : `${base} border-slate-200 shadow-sm shadow-black/5`;
  return to
    ? <Link to={to} className={cls} data-testid={testid}>{inner}</Link>
    : <div className={cls} data-testid={testid}>{inner}</div>;
}

function ChartCard({ title, subtitle, children, testid, tall }) {
  return (
    <Card className="p-5 bg-white border-slate-200 rounded-2xl shadow-sm shadow-black/5" data-testid={testid}>
      <div className="mb-3">
        <h3 className="font-heading text-base font-bold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div style={{ width: "100%", height: tall ? 320 : 240 }}>
        {children}
      </div>
    </Card>
  );
}

const TickStyle = { fontSize: 11, fill: "#64748B" };

function humanDate(d) {
  if (!d) return "";
  const parts = d.split("-");
  return `${parts[2]}/${parts[1]}`;
}

/** Fill nella serie giornaliera: se un giorno manca lo mette a 0 così le curve non "saltano". */
function fillSeries(daily, days) {
  const map = Object.fromEntries((daily || []).map((r) => [r.date, r.count]));
  const out = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    out.push({ date: iso, dateLabel: humanDate(iso), count: map[iso] || 0 });
  }
  return out;
}

export default function AdminStatsPage() {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/stats", { params: { days } });
      setStats(data);
    } catch (e) {
      setErr("Impossibile caricare le statistiche.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const signupsSeries = useMemo(() => fillSeries(stats?.users?.signups_daily, days), [stats, days]);
  const threadsSeries = useMemo(() => fillSeries(stats?.threads?.daily, days), [stats, days]);
  const messagesSeries = useMemo(() => fillSeries(stats?.messages?.daily, days), [stats, days]);

  const professionData = useMemo(() => (stats?.users?.by_profession || []).map((r) => ({
    name: ROLE_LABELS[r.key] || r.key || "Altro",
    value: r.count,
  })), [stats]);

  const cityData = useMemo(() => (stats?.users?.by_city || []).map((r) => ({
    name: r.key, value: r.count,
  })), [stats]);

  const specData = useMemo(() => (stats?.users?.by_specialization || []).map((r) => ({
    name: r.key, value: r.count,
  })), [stats]);

  const categoryData = useMemo(() => (stats?.threads?.by_category || []).map((r) => ({
    name: CATEGORY_LABELS[r.key] || r.key || "Altro", value: r.count,
  })), [stats]);

  const priorityData = useMemo(() => (stats?.threads?.by_priority || []).map((r) => ({
    name: PRIORITY_LABELS[r.key] || r.key || "Normale", value: r.count,
  })), [stats]);

  const statusData = useMemo(() => (stats?.threads?.by_status || []).map((r) => ({
    name: STATUS_LABELS[r.key]?.label || r.key || "Inviata", value: r.count,
  })), [stats]);

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-6" data-testid="admin-stats-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-[#1E3A8A]/80">
            Amministrazione
          </p>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
            Statistiche piattaforma
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Panoramica in tempo reale su utenti, richieste e movimento della rete Synappsi.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">Finestra:</label>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="w-40 h-9 bg-white border-slate-200" data-testid="admin-stats-days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Ultimi 7 giorni</SelectItem>
              <SelectItem value="30">Ultimi 30 giorni</SelectItem>
              <SelectItem value="90">Ultimi 90 giorni</SelectItem>
              <SelectItem value="365">Ultimo anno</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <SynapseLoader size="md" inline className="mr-2" /> Calcolo statistiche…
        </div>
      )}

      {err && !loading && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>
      )}

      {!loading && stats && (
        <>
          {/* KPI ROW 1 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <KpiCard icon={Users} label="Iscritti totali" value={stats.users.total}
                     sub={`${stats.users.verified} verificati · ${stats.users.pending} in attesa`}
                     color="text-[#1E3A8A]" testid="kpi-users"
                     to="/admin/verifiche?status=all" cta="Gestisci tutti i profili" />
            <KpiCard icon={MessageSquare} label="Richieste create" value={stats.threads.total}
                     sub={`${stats.threads.in_period} negli ultimi ${days}gg · ${stats.threads.open} aperte`}
                     color="text-[#10B981]" testid="kpi-threads"
                     to="/messaggi" cta="Vai ai messaggi" />
            <KpiCard icon={Activity} label="Messaggi scambiati" value={stats.messages.total}
                     sub={`${stats.messages.in_period} negli ultimi ${days}gg`}
                     color="text-[#0EA5E9]" testid="kpi-messages"
                     to="/messaggi" cta="Apri le conversazioni" />
            <KpiCard icon={PlaneTakeoff} label="MMG in ferie" value={stats.absences.active}
                     sub={`${stats.absences.pending} sostituzioni in attesa`}
                     color="text-[#F59E0B]" testid="kpi-absences"
                     to="/rubrica" cta="Chi è attualmente in ferie" />
          </div>

          {/* KPI ROW 2 - verification breakdown, tutti cliccabili */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-3 md:mt-4">
            <KpiCard icon={ShieldCheck} label="Verificati"  value={stats.users.verified}
                     color="text-emerald-600" testid="kpi-verified"
                     to="/admin/verifiche?status=verified" cta="Vedi profili approvati" />
            <KpiCard icon={Clock}       label="In attesa"   value={stats.users.pending}
                     color="text-amber-600" testid="kpi-pending"
                     to="/admin/verifiche?status=pending"
                     cta={stats.users.pending > 0 ? "Verifica ora →" : "Nessuno da verificare"} />
            <KpiCard icon={Users}       label="Rifiutati"   value={stats.users.rejected}
                     color="text-red-600" testid="kpi-rejected"
                     to="/admin/verifiche?status=rejected" cta="Rivedi i rifiuti" />
            <KpiCard icon={Users}       label="Sospesi"     value={stats.users.suspended}
                     color="text-slate-600" testid="kpi-suspended"
                     to="/admin/verifiche?status=suspended" cta="Gestisci sospesi" />
          </div>

          {/* Tip box quando ci sono professionisti in attesa */}
          {stats.users.pending > 0 && (
            <div className="mt-3 md:mt-4 p-3 md:p-4 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3"
                 data-testid="stats-pending-alert">
              <Clock className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-amber-900 text-sm">
                  {stats.users.pending} professionist{stats.users.pending === 1 ? "a in attesa" : "i in attesa"} di verifica
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Ogni giorno di attesa allontana un collega dalla piattaforma. Verifica ora i loro dati (FNOMCeO, Ordine, telefono).
                </div>
              </div>
              <Link to="/admin/verifiche?status=pending"
                    data-testid="stats-pending-cta"
                    className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white transition-colors">
                Apri elenco
              </Link>
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 mt-4">
            <ChartCard title="Nuove registrazioni per giorno"
                       subtitle={`Trend cumulativo — ultimi ${days} giorni`}
                       testid="chart-signups" tall>
              <ResponsiveContainer>
                <AreaChart data={signupsSeries} margin={{ top: 10, right: 12, left: -6, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gSignup" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="dateLabel" tick={TickStyle} interval="preserveStartEnd" />
                  <YAxis tick={TickStyle} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} labelStyle={{ color: "#0F172A" }} />
                  <Area type="monotone" dataKey="count" name="Iscritti" stroke={COLORS.primary} strokeWidth={2}
                        fill="url(#gSignup)" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Distribuzione per ruolo"
                       subtitle="Chi popola la piattaforma"
                       testid="chart-profession" tall>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={professionData} dataKey="value" nameKey="name" outerRadius={100} innerRadius={55}
                       paddingAngle={2} label={(e) => `${e.name}: ${e.value}`} labelLine={false}>
                    {professionData.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Top 10 città" subtitle="Sedi dei professionisti verificati" testid="chart-city" tall>
              {cityData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  <MapPin className="h-4 w-4 mr-2" /> Nessuna sede registrata ancora
                </div>
              ) : (
                <ResponsiveContainer>
                  <BarChart data={cityData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tick={TickStyle} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={TickStyle} width={100} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Bar dataKey="value" name="Professionisti" fill={COLORS.emerald} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Top 10 specializzazioni" testid="chart-spec" tall>
              {specData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  <Stethoscope className="h-4 w-4 mr-2" /> Nessuna specializzazione registrata
                </div>
              ) : (
                <ResponsiveContainer>
                  <BarChart data={specData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tick={TickStyle} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={TickStyle} width={120} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Bar dataKey="value" name="Professionisti" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Richieste create per giorno"
                       subtitle={`Volumi negli ultimi ${days} giorni`}
                       testid="chart-threads" tall>
              <ResponsiveContainer>
                <AreaChart data={threadsSeries} margin={{ top: 10, right: 12, left: -6, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gThreads" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.emerald} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={COLORS.emerald} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="dateLabel" tick={TickStyle} interval="preserveStartEnd" />
                  <YAxis tick={TickStyle} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Area type="monotone" dataKey="count" name="Richieste" stroke={COLORS.emerald} strokeWidth={2}
                        fill="url(#gThreads)" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Messaggi scambiati per giorno"
                       subtitle={`Attività di scambio negli ultimi ${days} giorni`}
                       testid="chart-messages" tall>
              <ResponsiveContainer>
                <LineChart data={messagesSeries} margin={{ top: 10, right: 12, left: -6, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="dateLabel" tick={TickStyle} interval="preserveStartEnd" />
                  <YAxis tick={TickStyle} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Line type="monotone" dataKey="count" name="Messaggi" stroke={COLORS.sky} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Categorie di richiesta" testid="chart-categories">
              <ResponsiveContainer>
                <BarChart data={categoryData} margin={{ top: 5, right: 12, left: -6, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ ...TickStyle, fontSize: 10 }} angle={-25} textAnchor="end" interval={0} height={60} />
                  <YAxis tick={TickStyle} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="value" name="Richieste" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Priorità e stato" testid="chart-priority-status">
              <div className="grid grid-cols-2 gap-2 h-full">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={priorityData} dataKey="value" nameKey="name" outerRadius={70} innerRadius={35}
                         label={(e) => e.value} labelLine={false}>
                      {priorityData.map((_, i) => (<Cell key={i} fill={[COLORS.slate, COLORS.emerald, COLORS.amber, COLORS.red][i % 4]} />))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={70} innerRadius={35}
                         label={(e) => e.value} labelLine={false}>
                      {statusData.map((_, i) => (<Cell key={i} fill={PALETTE[i % PALETTE.length]} />))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          <ChartCard title="Chi crea più richieste"
                     subtitle="Top 10 professionisti per numero di richieste create"
                     testid="chart-top-authors">
            {(!stats.top_authors || stats.top_authors.length === 0) ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Nessuna richiesta ancora creata
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={stats.top_authors.map(a => ({ name: a.name, value: a.count, prof: ROLE_LABELS[a.profession] || a.profession }))}
                          layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={TickStyle} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={TickStyle} width={140} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                           formatter={(value, name, props) => [`${value} richieste`, props.payload.prof || ""]} />
                  <Bar dataKey="value" name="Richieste" fill={COLORS.emerald} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Chi scrive più messaggi"
                     subtitle="Top 10 professionisti per volume di messaggi inviati"
                     testid="chart-top-messagers">
            {(!stats.top_messagers || stats.top_messagers.length === 0) ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Nessun messaggio ancora inviato
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={stats.top_messagers.map(a => ({ name: a.name, value: a.count, prof: ROLE_LABELS[a.profession] || a.profession }))}
                          layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={TickStyle} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={TickStyle} width={140} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                           formatter={(value, name, props) => [`${value} messaggi`, props.payload.prof || ""]} />
                  <Bar dataKey="value" name="Messaggi" fill={COLORS.sky} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          </div>

          <p className="text-[11px] text-slate-400 mt-4 text-right">
            Aggiornato al {new Date(stats.generated_at).toLocaleString("it-IT")}
          </p>
        </>
      )}
    </div>
  );
}
